import queryString from 'query-string'

import type { EMeasurementType } from '@web/entities/measurement'
import type {
  ICreatedPoint,
  IMeasuringPoint,
  TMeasuringPoint,
  TUpdatedPoint
} from '@web/entities/point'
import type { ILogicalExpression } from '@web/entities/work-mode/model/logical-expression.type'
import type { IChartIntegralPointResponse } from '@web/pages/results-integral-point/model/chart-integral-point.type'
import { ApiResource, ApiTag } from '@web/shared/constants/api.constants'
import { baseApi } from '@web/shared/infrastructure/api-base-query'
import type { ICopyDto } from '@web/shared/types/copy-dto.type'
import type { TId } from '@web/shared/types/id.type'
import type { TQueryParams } from '@web/shared/types/query-params.type'

export interface IGetPointsResponse {
  content: TMeasuringPoint[]
}

export const pointApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMeasuringPoints: builder.query<
      IGetPointsResponse,
      TQueryParams<{ machineId: TId }>
    >({
      query: (params) => {
        const query = queryString.stringify({ ...params })

        return {
          url: `${ApiResource.MeasuringPoints}?${query}`
        }
      },
      providesTags: [ApiTag.MeasuringPoints]
    }),

    createMeasuringPoints: builder.mutation<{ id: string }, ICreatedPoint>({
      query: (data) => ({
        url: ApiResource.MeasuringPoints,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [ApiTag.MeasuringPoints]
    }),

    getMeasuringPoint: builder.query<
      IMeasuringPoint,
      { measuringPointId: TId }
    >({
      query: ({ measuringPointId }) => ({
        url: `${ApiResource.MeasuringPoints}/${measuringPointId}`
      }),
      providesTags: [ApiTag.MeasuringPoint]
    }),

    updateMeasuringPoints: builder.mutation<
      null,
      { updatedData: TUpdatedPoint; pointId: TId }
    >({
      query: ({ updatedData, pointId }) => ({
        url: `${ApiResource.MeasuringPoints}/${pointId}`,
        method: 'PUT',
        body: updatedData
      }),
      invalidatesTags: [ApiTag.MeasuringPoints]
    }),

    deleteMeasuringPoint: builder.mutation<null, TId>({
      query: (id) => ({
        url: `${ApiResource.MeasuringPoints}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.MeasuringPoints]
    }),

    updateMeasuringPointsStatus: builder.mutation<
      null,
      { pointId: TId; statusData: { paused: boolean } }
    >({
      query: ({ pointId, statusData }) => ({
        url: `${ApiResource.MeasuringPoints}/${pointId}/paused`,
        method: 'PUT',
        body: statusData
      }),
      invalidatesTags: [ApiTag.GetAllNodes]
    }),

    updateMeasuringPointsReorder: builder.mutation<
      void,
      {
        pointId: string
        data: { order: number; parentId: string | null }
      }
    >({
      query: ({ pointId, data }) => ({
        url: `${ApiResource.MeasuringPoints}/${pointId}/reorder`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.MeasuringPoints]
    }),

    updateLogicExpressionMeasuringPoint: builder.mutation<
      null,
      { measuringPointId: TId; body: ILogicalExpression }
    >({
      query: ({ measuringPointId, body }) => ({
        url: `${ApiResource.MeasuringPoints}/${measuringPointId}/logic_expression`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.MeasuringPoint]
    }),

    deleteLogicExpressionPoint: builder.mutation<
      null,
      {
        pointId: TId
        level: number
      }
    >({
      query: ({ pointId, level }) => ({
        url: `${ApiResource.MeasuringPoints}/${pointId}/logic_expression/${level}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.MeasuringPoint]
    }),

    getIntegralScheme: builder.query<
      IChartIntegralPointResponse,
      {
        pointId: TId
        params?: {
          scalarSize?: number
          sortDir?: 'ASC' | 'DESC'
          workModes?: number[]
          types?: EMeasurementType[]
        }
      }
    >({
      query: ({ pointId, params }) => {
        const query = queryString.stringify(
          {
            scalarSize: params?.scalarSize,
            sortDir: params?.sortDir,
            workModes: params?.workModes,
            types: params?.types
          },
          { arrayFormat: 'comma' } // массивы → "1,3,5"
        )

        return {
          url: `${ApiResource.MeasuringPoints}/${pointId}/integral-scheme?${query}`
        }
      },
      providesTags: [ApiTag.MeasuringPoint]
    }),

    copyMeasuringPoint: builder.mutation<void, ICopyDto>({
      query: (body) => ({
        url: `${ApiResource.MeasuringPoints}/copy`,
        method: 'POST',
        body
      }),
      invalidatesTags: [
        ApiTag.MeasuringPoints,
        ApiTag.MeasuringPoint,
        ApiTag.Measurements,
        ApiTag.MeasurementsTree
      ]
    })
  })
})

export const {
  useGetMeasuringPointsQuery,
  useLazyGetMeasuringPointsQuery,
  useCreateMeasuringPointsMutation,
  useGetMeasuringPointQuery,
  useLazyGetMeasuringPointQuery,
  useUpdateMeasuringPointsMutation,
  useUpdateMeasuringPointsStatusMutation,
  useUpdateLogicExpressionMeasuringPointMutation,
  useDeleteLogicExpressionPointMutation,
  useUpdateMeasuringPointsReorderMutation,
  useDeleteMeasuringPointMutation,
  useGetIntegralSchemeQuery,
  useCopyMeasuringPointMutation
} = pointApi
